<!-- header-layout.component.html -->
<header
  class="h-14 sm:h-16 bg-white/95 dark:bg-stone-800/95 backdrop-blur-xl border-b border-grounded/20 dark:border-stone-700/30 shadow-2xl dark:shadow-fuchsia-900/10 sticky top-0 z-50 relative">
  <!-- Sacred Floating Orbs -->
  <div
    class="absolute top-2 left-20 w-6 h-6 bg-gradient-to-r from-soulfire/20 to-bloom/20 dark:from-fuchsia-600/20 dark:to-fuchsia-500/20 rounded-full blur-lg animate-pulse">
  </div>
  <div
    class="absolute top-3 right-32 w-4 h-4 bg-gradient-to-r from-bloom/15 to-soulfire/15 dark:from-fuchsia-500/15 dark:to-fuchsia-600/15 rounded-full blur-md animate-pulse">
  </div>

  <div class="h-full px-3 sm:px-6 flex items-center justify-between relative z-10">
    <!-- Logo Section -->
    <div class="flex items-center gap-3">
      <img src="assets/images/logo.png" alt="AI Chatbot Logo" class="h-8 sm:h-10 w-auto dark:brightness-125" />
      <div class="hidden md:block">
        <h1
          class="text-xl font-bold bg-gradient-to-r from-soulfire to-bloom dark:from-fuchsia-400 dark:to-fuchsia-300 bg-clip-text text-transparent"
          style="color: #761855">
          Coheras
        </h1>
        <p class="text-xs text-rooted/70 dark:text-stone-400 -mt-1">
          Your Thought Partner
        </p>
      </div>
    </div>

    <!-- Right Section -->
    <div class="flex items-center gap-4 relative" #dropdownContainer>
      <!-- Theme Toggle Button -->
      <button type="button" (click)="toggleTheme()" [class]="getThemeButtonClasses()" [title]="
          'Current theme: ' +
          themeDisplayName() +
          '. Click to switch to ' +
          nextThemeLabel()
        ">
        <!-- Theme Icon -->
        <i [class]="
            getThemeIconClass() +
            ' text-base transition-all duration-300 group-hover:scale-110'
          "></i>

        <!-- Sacred glow effect on hover -->
        <div
          class="absolute inset-0 rounded-xl bg-gradient-to-r from-soulfire/10 to-bloom/10 dark:from-fuchsia-600/10 dark:to-fuchsia-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
        </div>

        <!-- Theme indicator dot -->
        <div
          class="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full border border-white dark:border-stone-800 transition-all duration-300"
          [class]="getThemeIndicatorClass()"></div>
      </button>

      <!-- User Profile Button -->
      <div class="relative">
        <button type="button"
          class="flex items-center gap-3 px-4 py-2 hover:bg-sacred/50 dark:hover:bg-stone-700/50 rounded-xl transition-all duration-300 group border border-transparent hover:border-grounded/20 dark:hover:border-stone-600/30"
          (click)="toggleDropdown($event)" [attr.aria-expanded]="isDropdownOpen()" aria-haspopup="true">
          <!-- Avatar -->
          <div
            class="w-9 h-9 bg-gradient-to-br from-soulfire to-bloom dark:from-fuchsia-600 dark:to-fuchsia-500 rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-md ring-2 ring-white dark:ring-stone-700 group-hover:ring-bloom/30 dark:group-hover:ring-fuchsia-500/30 transition-all duration-300">
            {{ userInitials() }}
          </div>

          <!-- User Info -->
          <div class="hidden sm:block text-left max-w-[150px]">
            <p class="text-sm font-semibold text-rooted dark:text-stone-200 truncate group-hover:text-soulfire dark:group-hover:text-fuchsia-400 transition-colors duration-300"
              [title]="userName()">
              {{ userName() }}
            </p>
            <div class="flex items-center gap-2">
              <p class="text-xs text-rooted/60 dark:text-stone-400">
                {{ userRoleLabel() }}
              </p>
              <span
                class="inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-soulfire dark:text-fuchsia-400 bg-gradient-to-r from-sacred to-bloom/20 dark:from-stone-700/50 dark:to-fuchsia-500/20 border border-soulfire/20 dark:border-fuchsia-500/20 rounded-full">
                <i class="pi pi-star text-xs mr-1"></i>
                {{ userSubscriptionLabel() }}
              </span>
            </div>
          </div>

          <!-- Chevron -->
          <i class="pi pi-chevron-down text-rooted/40 dark:text-stone-400 text-xs transition-all duration-300 group-hover:text-soulfire dark:group-hover:text-fuchsia-400"
            [class.rotate-180]="isDropdownOpen()" [class.text-soulfire]="isDropdownOpen()"
            [class.dark:text-fuchsia-400]="isDropdownOpen()"></i>
        </button>

        <!-- Sacred Dropdown Menu -->
        <div *ngIf="isDropdownOpen()"
          class="absolute right-0 top-full mt-3 w-80 bg-white/90 dark:bg-stone-800/90 backdrop-blur-3xl rounded-3xl border border-grounded/20 dark:border-stone-700/50 shadow-[0_20px_60px_rgba(0,0,0,0.15)] dark:shadow-[0_20px_60px_rgba(217,70,239,0.15)] z-[200] origin-top-right transition-all duration-300 ease-out animate-in overflow-hidden"
          role="menu" aria-label="User menu">
          <!-- Sacred Header -->
          <div
            class="px-6 py-4 bg-white/40 dark:bg-stone-700/40 border-b border-white/20 dark:border-stone-600/30 relative overflow-hidden">
            <!-- Floating orb -->
            <div class="absolute -top-4 -right-4 w-16 h-16 bg-soulfire/20 dark:bg-fuchsia-600/20 rounded-full blur-2xl">
            </div>

            <div class="flex items-center justify-center relative z-10">
              <p class="text-sm font-medium text-rooted dark:text-stone-200 truncate" [title]="userEmail()">
                {{ userEmail() }}
              </p>
            </div>
          </div>

          <!-- Sacred Navigation -->
          <div class="py-3">
            <!-- Theme Selection Item (in dropdown) -->
            <div class="px-6 py-2 border-b border-white/10 dark:border-stone-600/20 mb-2">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div
                    class="w-8 h-8 rounded-xl flex items-center justify-center bg-gradient-to-r from-soulfire/20 to-bloom/20 dark:from-fuchsia-600/20 dark:to-fuchsia-400/20">
                    <i [class]="
                        getThemeIconClass() +
                        ' text-sm text-soulfire dark:text-fuchsia-400'
                      "></i>
                  </div>
                  <span class="text-xs font-medium text-rooted/70 dark:text-stone-400">Theme: {{ themeDisplayName()
                    }}</span>
                </div>
                <button type="button" (click)="toggleTheme()"
                  class="text-xs px-2 py-1 rounded-lg bg-sacred/20 dark:bg-stone-700/50 text-soulfire dark:text-fuchsia-400 hover:bg-sacred/30 dark:hover:bg-stone-700/70 transition-colors duration-200">
                  Switch
                </button>
              </div>
            </div>

            <!-- Profile -->
            <button type="button"
              class="w-full flex items-center gap-4 px-6 py-4 text-left text-rooted dark:text-stone-200 hover:bg-sacred/10 dark:hover:bg-stone-700/50 transition-all duration-300 group relative overflow-hidden"
              (click)="navigateToProfile()" role="menuitem">
              <div
                class="absolute inset-0 bg-rooted/5 dark:bg-stone-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              </div>
              <div
                class="w-10 h-10 rounded-2xl flex items-center justify-center bg-rooted/10 dark:bg-stone-700/50 text-sacred dark:text-stone-300 group-hover:bg-rooted/20 dark:group-hover:bg-stone-600/60 transition-all duration-300 shadow-sm relative z-10">
                <i class="pi pi-user text-base"></i>
              </div>
              <div class="flex-1 relative z-10">
                <p class="font-semibold text-sm mb-0.5">Profile</p>
                <!-- <p class="text-xs text-rooted/60 dark:text-stone-400">
                  View and edit your profile
                </p> -->
              </div>
              <i
                class="pi pi-chevron-right text-xs text-rooted/40 dark:text-stone-400 group-hover:text-rooted/60 dark:group-hover:text-stone-300 transition-colors duration-200 relative z-10"></i>
            </button>

            <!-- Settings -->
            @if (isAdmin() || isSubadmin()) {
            <button type="button"
              class="w-full flex items-center gap-4 px-6 py-4 text-left text-rooted dark:text-stone-200 hover:bg-bloom/10 dark:hover:bg-fuchsia-500/10 transition-all duration-300 group relative overflow-hidden"
              (click)="navigateToSettings()" role="menuitem">
              <div
                class="absolute inset-0 bg-bloom/5 dark:bg-fuchsia-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              </div>
              <div
                class="w-10 h-10 rounded-2xl flex items-center justify-center bg-bloom/10 dark:bg-fuchsia-500/20 text-bloom dark:text-fuchsia-400 group-hover:bg-bloom/20 dark:group-hover:bg-fuchsia-500/30 transition-all duration-300 shadow-sm relative z-10">
                <i class="pi pi-cog text-base"></i>
              </div>
              <div class="flex-1 relative z-10">
                <p class="font-semibold text-sm mb-0.5">Settings</p>
                <!-- <p class="text-xs text-rooted/60 dark:text-stone-400">
                  Configure your preferences
                </p> -->
              </div>
              <i
                class="pi pi-chevron-right text-xs text-rooted/40 dark:text-stone-400 group-hover:text-rooted/60 dark:group-hover:text-stone-300 transition-colors duration-200 relative z-10"></i>
            </button>
            }

            <!-- Help & Support -->
            <button type="button"
              class="w-full flex items-center gap-4 px-6 py-4 text-left text-rooted dark:text-stone-200 hover:bg-soulfire/10 dark:hover:bg-fuchsia-600/10 transition-all duration-300 group relative overflow-hidden"
              role="menuitem">
              <div
                class="absolute inset-0 bg-soulfire/5 dark:bg-fuchsia-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              </div>
              <div
                class="w-10 h-10 rounded-2xl flex items-center justify-center bg-soulfire/10 dark:bg-fuchsia-600/20 text-soulfire dark:text-fuchsia-400 group-hover:bg-soulfire/20 dark:group-hover:bg-fuchsia-600/30 transition-all duration-300 shadow-sm relative z-10">
                <i class="pi pi-compass text-base"></i>
              </div>
              <div class="flex-1 relative z-10">
                <p class="font-semibold text-sm mb-0.5">Help & Support</p>
                <!-- <p class="text-xs text-rooted/60 dark:text-stone-400">
                  Get help and support
                </p> -->
              </div>
              <i
                class="pi pi-chevron-right text-xs text-rooted/40 dark:text-stone-400 group-hover:text-rooted/60 dark:group-hover:text-stone-300 transition-colors duration-200 relative z-10"></i>
            </button>

            <!-- Divider -->
            <div class="mx-6 my-4 relative">
              <div class="border-t border-grounded/20 dark:border-stone-600/30"></div>
              <div class="absolute inset-0 flex items-center justify-center">
                <div class="bg-white dark:bg-stone-800 px-3">
                  <span class="text-xs text-rooted/40 dark:text-stone-500">•</span>
                </div>
              </div>
            </div>

            <!-- Logout -->
            <button type="button"
              class="w-full flex items-center gap-4 px-6 py-4 text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-300 group relative overflow-hidden"
              (click)="logout()" role="menuitem">
              <div
                class="absolute inset-0 bg-red-50 dark:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              </div>
              <div
                class="w-10 h-10 rounded-2xl flex items-center justify-center bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 group-hover:bg-red-100 dark:group-hover:bg-red-900/40 transition-all duration-300 shadow-sm relative z-10">
                <i class="pi pi-sign-out text-base"></i>
              </div>
              <div class="flex-1 relative z-10">
                <p class="font-semibold text-sm mb-0.5">Sign Out</p>
                <p class="text-xs text-red-500/70 dark:text-red-400/70">
                  Sign out of your account
                </p>
              </div>
              <i
                class="pi pi-chevron-right text-xs text-red-400/60 dark:text-red-400/60 group-hover:text-red-500/80 dark:group-hover:text-red-400/80 transition-colors duration-200 relative z-10"></i>
            </button>
          </div>

          <!-- Sacred Footer -->
          <!-- <div
            class="px-6 py-3 bg-white/30 dark:bg-stone-700/30 border-t border-white/10 dark:border-stone-600/20"
          >
            <p
              class="text-center text-xs text-rooted/50 dark:text-stone-400 leading-relaxed"
            >
              AI Chatbot - Intelligent Assistant
            </p>
          </div> -->
        </div>
      </div>
    </div>
  </div>

  <!-- Logout Loading Overlay -->
  <div *ngIf="showLogoutOverlay()"
    class="fixed inset-0 bg-rooted/50 dark:bg-stone-900/70 backdrop-blur-sm z-[300] flex items-center justify-center">
    <div
      class="bg-white/95 dark:bg-stone-800/95 backdrop-blur-xl rounded-2xl p-8 shadow-2xl dark:shadow-fuchsia-900/20 text-center max-w-sm mx-4 border border-grounded/20 dark:border-stone-700/50">
      <div
        class="w-16 h-16 bg-gradient-to-r from-soulfire to-bloom dark:from-fuchsia-600 dark:to-fuchsia-500 rounded-full flex items-center justify-center mx-auto mb-4">
        <i class="pi pi-spinner pi-spin text-white text-2xl"></i>
      </div>
      <h3 class="text-lg font-semibold text-rooted dark:text-stone-100 mb-2">
        Signing Out
      </h3>
      <p class="text-rooted/70 dark:text-stone-300">
        Please wait while we log you out...
      </p>
    </div>
  </div>
</header>